import {useEffect, useMemo} from 'react';
import type {ColumnSet} from 'services/advancedSearch/types';
import {
  useCreateColumnSetMutation,
  useDeleteColumnSetMutation,
  useGetColumnSetsQuery,
  useUpdateColumnSetMutation,
} from 'services/advancedSearch/advancedSearchApi';
import {mapRtkErrorToHttpError, queryStatusToLoadStatus} from 'store/api';
import {useSnacks} from 'components';
import type {ColumnSetsType} from '../types';

export const useColumnSets = (): ColumnSetsType => {
  const {errorSnack} = useSnacks();
  const {data, status: loadStatus} = useGetColumnSetsQuery();
  const [createColumnSet, {error: createError}] = useCreateColumnSetMutation();
  const [updateColumnSet, {error: updateError}] = useUpdateColumnSetMutation();
  const [deleteColumnSet, {error: deleteError}] = useDeleteColumnSetMutation();

  useEffect(() => {
    if (createError) {
      errorSnack(
        'Creating column set failed',
        mapRtkErrorToHttpError(createError),
      );
    }
    if (updateError) {
      errorSnack(
        'Updating column set failed',
        mapRtkErrorToHttpError(updateError),
      );
    }
    if (deleteError) {
      errorSnack(
        'Deleting column set failed',
        mapRtkErrorToHttpError(deleteError),
      );
    }
  }, [createError, deleteError, errorSnack, updateError]);

  return useMemo(() => {
    const onCreate = async (columnSet: Omit<ColumnSet, 'id'>) => {
      await createColumnSet(columnSet);
    };
    const onUpdate = async (columnSet: ColumnSet) => {
      await updateColumnSet(columnSet);
    };
    const onDelete = async (columnSet: Pick<ColumnSet, 'id'>) => {
      await deleteColumnSet(columnSet);
    };

    return {
      columnSets: data ?? [],
      statusColumnSets: queryStatusToLoadStatus(loadStatus),
      createColumnSet: onCreate,
      editColumnSet: onUpdate,
      deleteColumnSet: onDelete,
    };
  }, [data, createColumnSet, deleteColumnSet, loadStatus, updateColumnSet]);
};
